import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Card,
  Button,
  Badge,
  List,
  Loading,
  Icon,
  Callout,
} from '@folio/stripes/components';

import ProfessionalPrinterHelper from './ProfessionalPrinterHelper';
import EnhancedPrinterDiscovery from './EnhancedPrinterDiscovery'; // kware: إضافة خدمة الاكتشاف المحسنة
import DirectConnectionManager from './DirectConnectionManager'; // kware: إضافة مدير الاتصال المباشر

const PrinterDetection = ({
  onPrinterSelected,
  selectedPrinter,
  discoveredPrinters = [], // kware: قائمة الطابعات المكتشفة من الحالة العامة
  onPrintersDiscovered // kware: معالج تحديث قائمة الطابعات
}) => {
  const intl = useIntl();
  const [isScanning, setIsScanning] = useState(false);
  const [detectedPrinters, setDetectedPrinters] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [supportedAPIs, setSupportedAPIs] = useState({});
  const [discoveryResults, setDiscoveryResults] = useState(null);
  // kware: استبدال ProfessionalPrinterHelper بالخدمات المحسنة
  const printerHelper = new ProfessionalPrinterHelper();
  const discoveryService = new EnhancedPrinterDiscovery();
  const connectionManager = new DirectConnectionManager();

  // Check supported APIs on component mount
  useEffect(() => {
    // kware: استخدام خدمة الاكتشاف المحسنة للحصول على قدرات المتصفح
    const capabilities = discoveryService.getBrowserCapabilities();
    setSupportedAPIs(capabilities);
  }, []);

  /**
   * Enhanced scan for available printers
   * kware: استبدال منطق المسح القديم بالخدمة المحسنة + تحديث الحالة العامة
   */
  const scanForPrinters = async () => {
    setIsScanning(true);
    setDetectedPrinters([]);

    try {
      const results = await discoveryService.discoverPrinters();
      setDiscoveryResults(results);
      setDetectedPrinters(results.printers);

      // Update supported APIs with actual capabilities
      setSupportedAPIs(results.capabilities);

      // kware: تحديث الحالة العامة للطابعات المكتشفة
      if (onPrintersDiscovered) {
        onPrintersDiscovered(results.printers);
      }
    } catch (error) {
      console.error('Enhanced printer scan error:', error);
      // Fallback to empty list
      setDetectedPrinters([]);
      if (onPrintersDiscovered) {
        onPrintersDiscovered([]);
      }
    } finally {
      setIsScanning(false);
    }
  };

  /**
   * Get printer name from USB device
   */
  const getUSBPrinterName = (device) => {
    const vendors = {
      0x0A5F: 'Zebra Technologies',
      0x04F9: 'Brother Industries',
      0x04B8: 'Epson',
      0x1504: 'Citizen',
      0x0619: 'Star Micronics'
    };

    const vendorName = vendors[device.vendorId] || `Unknown (${device.vendorId.toString(16)})`;
    return `${vendorName} Printer`;
  };

  /**
   * Get recommended printer types by vendor
   */
  const getPrinterTypeByVendor = (vendorId) => {
    const mapping = {
      0x0A5F: ['ZEBRA_ZPL'], // Zebra
      0x04F9: ['BROTHER_PTOUCH'], // Brother
      0x04B8: ['ESC_POS'], // Epson
      0x1504: ['ESC_POS'], // Citizen
      0x0619: ['ESC_POS']  // Star
    };

    return mapping[vendorId] || ['ESC_POS'];
  };

  /**
   * Enhanced connect to selected printer
   * kware: استبدال منطق الاتصال القديم بمدير الاتصال المحسن
   */
  const connectToPrinter = async (printer) => {
    setConnectionStatus('connecting');

    try {
      let connection;

      if (printer.type === 'usb' || printer.type === 'usb-discover') {
        // USB connection
        connection = await connectionManager.connectUSBPrinter(
          printer.device,
          { maxRetries: 3 }
        );
      } else if (printer.type === 'serial' || printer.type === 'serial-discover') {
        // Serial connection
        connection = await connectionManager.connectSerialPrinter(
          printer.port,
          {
            maxRetries: 3,
            name: printer.name,
            baudRate: 9600
          }
        );
      } else if (printer.type === 'manual' || printer.action === 'discover') {
        // Manual connection - prompt user
        if (printer.id.includes('serial')) {
          connection = await connectionManager.connectSerialPrinter();
        } else {
          connection = await connectionManager.connectUSBPrinter();
        }
      } else {
        // For network/file-based printers, create a virtual connection
        connection = {
          id: printer.id,
          name: printer.name,
          type: printer.type,
          connected: true,
          virtual: true
        };
      }

      if (connection) {
        setConnectionStatus('connected');

        // Save successful connection
        if (!connection.virtual) {
          discoveryService.savePrinter({
            ...printer,
            connectionId: connection.id,
            lastConnected: new Date()
          });
        }

        onPrinterSelected({
          ...printer,
          connection
        });
      } else {
        throw new Error('Connection failed');
      }
    } catch (error) {
      setConnectionStatus('error');
      console.error('Enhanced connection error:', error);
    }
  };

  /**
   * Enhanced test printer connection
   * kware: استبدال اختبار الطابعة بمدير الاتصال المحسن
   */
  const testPrinter = async (printer) => {
    try {
      if (printer.connection && printer.connection.id) {
        // Test using connection manager
        return await connectionManager.testConnection(
          printer.connection.id,
          '^XA^FO50,50^A0N,50,50^FDTest^FS^XZ' // ZPL test command
        );
      } else {
        // Fallback to old method
        const testLabel = '^XA^FO50,50^A0N,50,50^FDTest^FS^XZ';
        await printerHelper.sendToPrinter(testLabel);

        return {
          success: true,
          message: 'Test print sent successfully'
        };
      }
    } catch (error) {
      return {
        success: false,
        message: error.message
      };
    }
  };

  /**
   * Get status badge color
   */
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'available': return 'success';
      case 'connected': return 'primary';
      case 'error': return 'red';
      case 'manual': return 'default';
      default: return 'default';
    }
  };

  /**
   * Get connection icon
   */
  const getConnectionIcon = (connectionType) => {
    switch (connectionType) {
      case 'USB Direct':
      case 'USB': return 'lightning';
      case 'USB-Serial':
      case 'Serial': return 'link';
      case 'Bluetooth': return 'graph';
      default: return 'desktop';
    }
  };

  return (
    <Card>
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h4 style={{ margin: 0 }}>
            <FormattedMessage
              id="ui-plugin-inventory-templates.printerDetection.title"
              defaultMessage="Printer Detection"
            />
          </h4>

          <Button
            buttonStyle="primary"
            onClick={scanForPrinters}
            disabled={isScanning}
          >
            {isScanning ? (
              <Loading />
            ) : (
              <FormattedMessage
                id="ui-plugin-inventory-templates.printerDetection.scan"
                defaultMessage="Scan for Printers"
              />
            )}
          </Button>
        </div>

        {/* Enhanced Supported APIs Info */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '16px',
          fontSize: '12px'
        }}
        >
          <strong>
            <FormattedMessage
              id="ui-plugin-inventory-templates.printerDetection.apiSupport"
              defaultMessage="Browser Support:"
            />
          </strong>
          <div style={{ marginTop: '4px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Badge color={supportedAPIs.webSerial ? 'success' : 'red'}>
              Web Serial: {supportedAPIs.webSerial ? '✓' : '✗'}
            </Badge>
            <Badge color={supportedAPIs.webUSB ? 'success' : 'red'}>
              WebUSB: {supportedAPIs.webUSB ? '✓' : '✗'}
            </Badge>
            <Badge color={supportedAPIs.webBluetooth ? 'success' : 'red'}>
              Bluetooth: {supportedAPIs.webBluetooth ? '✓' : '✗'}
            </Badge>
            {/* kware: إضافة معلومات قدرات إضافية */}
            <Badge color={supportedAPIs.serviceWorker ? 'success' : 'default'}>
              Service Worker: {supportedAPIs.serviceWorker ? '✓' : '✗'}
            </Badge>
            <Badge color={supportedAPIs.fileAPI ? 'success' : 'default'}>
              File API: {supportedAPIs.fileAPI ? '✓' : '✗'}
            </Badge>
          </div>

          {/* Discovery Results Summary */}
          {discoveryResults && (
            <div style={{ marginTop: '8px', fontSize: '11px', color: '#666' }}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.printerDetection.discovered"
                defaultMessage="Found: {usb} USB, {serial} Serial, {manual} Manual, {saved} Saved"
                values={discoveryResults.counts}
              />
            </div>
          )}
        </div>

        {/* Detected Printers List - kware: استخدام الطابعات من الحالة العامة أو المحلية */}
        {(discoveredPrinters.length > 0 || detectedPrinters.length > 0) && (
          <div>
            <h5>
              <FormattedMessage
                id="ui-plugin-inventory-templates.printerDetection.detected"
                defaultMessage="Detected Printers ({count})"
                values={{ count: (discoveredPrinters.length > 0 ? discoveredPrinters.length : detectedPrinters.length) }}
              />
            </h5>

            <List
              items={discoveredPrinters.length > 0 ? discoveredPrinters : detectedPrinters} // kware: أعطي أولوية للطابعات من الحالة العامة
              itemFormatter={(printer) => (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  border: selectedPrinter?.id === printer.id ? '2px solid #0071bc' : '1px solid #ddd',
                  borderRadius: '4px',
                  marginBottom: '8px'
                }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Icon
                      icon={getConnectionIcon(printer.connectionType)}
                      size="medium"
                    />
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{printer.name}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {printer.connectionType}
                        {/* kware: إضافة معلومات محسنة للطابعة */}
                        {printer.capabilities && (
                          <span> - {printer.capabilities.join(', ')}</span>
                        )}
                        {printer.description && (
                          <div style={{ fontSize: '10px', fontStyle: 'italic', marginTop: '2px' }}>
                            {printer.description}
                          </div>
                        )}
                        {printer.lastUsed && (
                          <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                            آخر استخدام: {new Date(printer.lastUsed).toLocaleString('ar')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Badge color={getStatusBadgeColor(printer.status)}>
                      {printer.status}
                    </Badge>

                    {selectedPrinter?.id !== printer.id ? (
                      <Button
                        buttonStyle="default"
                        buttonClass="primary"
                        onClick={() => connectToPrinter(printer)}
                        disabled={connectionStatus === 'connecting'}
                      >
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.printerDetection.connect"
                          defaultMessage="Connect"
                        />
                      </Button>
                    ) : (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <Button
                          buttonStyle="default"
                          onClick={() => testPrinter(printer)}
                        >
                          <FormattedMessage
                            id="ui-plugin-inventory-templates.printerDetection.test"
                            defaultMessage="Test"
                          />
                        </Button>
                        <Badge color="success">
                          <FormattedMessage
                            id="ui-plugin-inventory-templates.printerDetection.connected"
                            defaultMessage="Connected"
                          />
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              )}
            />
          </div>
        )}

        {/* Connection Instructions */}
        {(!supportedAPIs.webSerial && !supportedAPIs.webUSB) && (
          <Callout type="warning">
            <FormattedMessage
              id="ui-plugin-inventory-templates.printerDetection.unsupported"
              defaultMessage="Your browser doesn't support direct printer connection. Please use the 'Download Print File' option instead."
            />
          </Callout>
        )}

        {/* kware: عرض حالة عدم وجود طابعات بشكل صحيح */}
        {(discoveredPrinters.length === 0 && detectedPrinters.length === 0) && !isScanning && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            <Icon icon="desktop" size="large" style={{ marginBottom: '8px' }} />
            <div>
              <FormattedMessage
                id="ui-plugin-inventory-templates.printerDetection.noPrinters"
                defaultMessage="لا توجد طابعات مكتشفة"
              />
            </div>
            <div style={{ fontSize: '12px', marginTop: '8px' }}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.printerDetection.scanHelp"
                defaultMessage="اضغط 'البحث عن الطابعات' للبحث عن الأجهزة المتصلة"
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

PrinterDetection.propTypes = {
  onPrinterSelected: PropTypes.func.isRequired,
  selectedPrinter: PropTypes.object,
  discoveredPrinters: PropTypes.arrayOf(PropTypes.object), // kware: قائمة الطابعات المكتشفة
  onPrintersDiscovered: PropTypes.func, // kware: معالج اكتشاف الطابعات
};

export default PrinterDetection;
